import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';

import { NgFor, NgIf } from '@angular/common';
import { DateRangePickerComponent } from 'app/shared/date-range-picker/date-range-picker.component';

const customPresets = [
  'today',
  'this week',
  'this month',
  'this year',
  'last week',
  'last month',
  'last year',
] as const;

type CustomPreset = (typeof customPresets)[number];

@Component({
  selector: 'app-time-period-panel',
  templateUrl: './time-period-panel.component.html',
  styleUrls: ['./time-period-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, NgFor, NgIf],
  standalone: true,
})
export class TimePeriodPanelComponent<D> {
  @HostBinding('class.touch-ui')
  readonly customPresets = customPresets;
  readonly isTouchUi = true;

  constructor(
    private dateAdapter: DateAdapter<any>,
    private picker: DateRangePickerComponent,
  ) {}

  private get today(): any {
    const today = this.dateAdapter.getValidDateOrNull(new Date());
    if (today === null) {
      throw new Error('date creation failed');
    }
    return today;
  }

  // called when user selects a range preset:
  selectPeriod(period: CustomPreset): void {
    let [start, end] = this.calculateDateRange(period);
    start.setHours(0, 0, 0);
    end.setHours(23, 59, 59); //set to end of day
    this.picker.setRange(start, end);
    this.picker.close();
  }

  private calculateDateRange(periodName: CustomPreset): [start: Date, end: Date] {
    const today = this.today;
    //need separate instance to allow hh:MM:SS manipulation
    const endOfDay = this.dateAdapter.getValidDateOrNull(new Date());
    const year = this.dateAdapter.getYear(today);

    switch (periodName) {
      case 'today':
        return [today, endOfDay];
      case 'this week': {
        return this.calculateWeek(today);
      }
      case 'this month': {
        return this.calculateMonth(today);
      }
      case 'this year': {
        const start = this.dateAdapter.createDate(year, 0, 1);
        const end = this.dateAdapter.createDate(year, 11, 31);
        return [start, end];
      }
      case 'last week': {
        const thisDayLastWeek = this.dateAdapter.addCalendarDays(today, -7);
        return this.calculateWeek(thisDayLastWeek);
      }
      case 'last month': {
        const thisDayLastMonth = this.dateAdapter.addCalendarMonths(today, -1);
        return this.calculateMonth(thisDayLastMonth);
      }
      case 'last year': {
        const start = this.dateAdapter.createDate(year - 1, 0, 1);
        const end = this.dateAdapter.createDate(year - 1, 11, 31);
        return [start, end];
      }
      default:
        // exhaustiveness check;
        return periodName;
    }
  }

  private calculateMonth(forDay: any): [start: any, end: any] {
    const year = this.dateAdapter.getYear(forDay);
    const month = this.dateAdapter.getMonth(forDay);
    const start = this.dateAdapter.createDate(year, month, 1);
    const end = this.dateAdapter.addCalendarDays(start, this.dateAdapter.getNumDaysInMonth(forDay) - 1);
    return [start, end];
  }

  private calculateWeek(forDay: any): [start: any, end: any] {
    const deltaStart = this.dateAdapter.getFirstDayOfWeek() - this.dateAdapter.getDayOfWeek(forDay);
    const start = this.dateAdapter.addCalendarDays(forDay, deltaStart);
    const end = this.dateAdapter.addCalendarDays(start, 6);
    return [start, end];
  }
}
